// vim: set ts=2 sts=2 sw=2 et:

// Datos inventados para iterar diseños en /lab sin live server ni operador. Nombres y cuentas son ficticios.

import type { Lift } from "../../types/dataTypes";
import type {
  JudgeLights,
  PlatformLiveAttempt,
  PlatformLiveAttemptAthleteMeta,
  PlatformLiveAttemptSeriesItem,
  PlatformLiveLiftSeries,
} from "../live/platformLiveProtocol";

export interface LabScenario {
  id: string;
  label: string;
  attempt: PlatformLiveAttempt | null;
  /** Veredicto en pantalla (ventana de reveal); null mientras el intento está en curso. */
  reveal: "good" | "noLift" | null;
  judgeLights: JudgeLights;
  /** Segundos restantes al abrir la escena; null = reloj en espera (60). */
  clockSeconds: number | null;
  waitingForNext?: boolean;
}

// `name` es el respaldo del lab: en producción el nombre sale de `meet.name` (lo carga el bundle de la web).
export const LAB_TOURNAMENT = {
  name: "Campeonato Iberoamericano WRPF 2026",
  day: 1,
  platformId: 2,
};

const off: JudgeLights = { J1: "off", J2: "off", J3: "off" };

const ok = (weightKg: number, extra: Partial<PlatformLiveAttemptSeriesItem> = {}): PlatformLiveAttemptSeriesItem => ({
  weightKg,
  status: "good",
  ...extra,
});
const no = (weightKg: number, extra: Partial<PlatformLiveAttemptSeriesItem> = {}): PlatformLiveAttemptSeriesItem => ({
  weightKg,
  status: "noLift",
  ...extra,
});
const next = (weightKg = 0, extra: Partial<PlatformLiveAttemptSeriesItem> = {}): PlatformLiveAttemptSeriesItem => ({
  weightKg,
  status: "pending",
  ...extra,
});
const untouched = (): Array<PlatformLiveAttemptSeriesItem> => [next(), next(), next()];

const makeAttempt = (
  entryId: number,
  lifterName: string,
  lift: Lift,
  attemptNumber: number,
  meta: Omit<PlatformLiveAttemptAthleteMeta, "lift">,
  liftSeries: Array<PlatformLiveLiftSeries>,
  record?: Pick<PlatformLiveAttempt, "recordAttempt" | "recordCategory">,
): PlatformLiveAttempt => {
  const current = liftSeries.find((series) => series.lift === lift);
  const weightKg = current?.attempts[attemptNumber - 1]?.weightKg ?? 0;
  return {
    key: `${LAB_TOURNAMENT.day}:${LAB_TOURNAMENT.platformId}:${entryId}:${lift}:${attemptNumber}`,
    day: LAB_TOURNAMENT.day,
    platformId: LAB_TOURNAMENT.platformId,
    entryId,
    lift,
    attempt: attemptNumber,
    lifterName,
    weightKg,
    ...record,
    athleteMeta: { ...meta, lift },
    attemptSeries: current?.attempts,
    liftSeries,
  };
};

const valentina = makeAttempt(
  11,
  "Valentina Sosa",
  "S",
  1,
  {
    team: "Norte Barbell",
    category: "Open",
    age: 27,
    ageDivision: "Open",
    equipment: "Bare",
    weightClassKg: "67.5",
    location: { city: "San Miguel de Tucumán", state: "Tucumán", country: "Argentina" },
    bodyweightKg: 66.8,
    instagram: "valentina.lab",
    primaryClassification: { equipment: "Bare", event: "Full Powerlifting RAW", division: "Open", weightClass: "67.5" },
    personalBestsKg: { squat: 125, bench: 70, deadlift: 150 },
  },
  [
    { lift: "S", attempts: [next(130), next(), next()] },
    { lift: "B", attempts: untouched() },
    { lift: "D", attempts: untouched() },
  ],
);

// El caso de la maqueta: sentadilla 200, banco 150 válido y 160 en curso.
const joaquin = makeAttempt(
  12,
  "Joaquín Ferreyra",
  "B",
  2,
  {
    team: "Fuerza Litoral",
    category: "Junior 20-23",
    age: 22,
    ageDivision: "Junior 20-23",
    equipment: "Bare",
    weightClassKg: "100",
    location: { city: "Corrientes", state: "Corrientes", country: "Argentina" },
    bodyweightKg: 98.4,
    instagram: "joaquin.lab",
    primaryClassification: { equipment: "Bare", event: "Full Powerlifting RAW", division: "Junior 20-23", weightClass: "100" },
    personalBestsKg: { squat: 205, bench: 155, deadlift: 240 },
  },
  [
    { lift: "S", attempts: [ok(185), ok(200), no(210)] },
    { lift: "B", attempts: [ok(150), next(160), next()] },
    { lift: "D", attempts: untouched() },
  ],
);

const florencia = makeAttempt(
  13,
  "María Florencia Echeverría Santángelo",
  "D",
  3,
  {
    team: "Andes Barbell",
    category: "Master 40-44",
    age: 42,
    ageDivision: "Master 40-44",
    equipment: "Wraps",
    weightClassKg: "75",
    location: { city: "Mérida", state: "Mérida", country: "Venezuela, Bolivarian Republic of" },
    bodyweightKg: 74.2,
    instagram: "florencia.lab",
    primaryClassification: { equipment: "Wraps", event: "Full Powerlifting Vendas", division: "Master 40-44", weightClass: "75" },
    personalBestsKg: { squat: 150, bench: 85, deadlift: 192.5 },
  },
  [
    { lift: "S", attempts: [ok(140), ok(150), ok(155, { recordAttempt: true, recordCategory: "REGIONAL" })] },
    { lift: "B", attempts: [ok(80), no(85), ok(85)] },
    { lift: "D", attempts: [ok(180), ok(190), next(197.5, { recordAttempt: true, recordCategory: "NATIONAL" })] },
  ],
  { recordAttempt: true, recordCategory: "NATIONAL" },
);

const lucas = makeAttempt(
  14,
  "Lucas Benítez",
  "B",
  3,
  {
    team: "",
    category: "Open",
    age: 31,
    ageDivision: "Open",
    equipment: "Bare",
    weightClassKg: "90",
    events: ["B"],
    location: { city: "Rosario", state: "Santa Fe" },
    bodyweightKg: 89.1,
    primaryClassification: { equipment: "Bare", event: "Press de Banca RAW", division: "Open", weightClass: "90" },
    personalBestsKg: { bench: 175 },
  },
  [{ lift: "B", attempts: [ok(170), no(177.5), next(177.5)] }],
);

const tomas = makeAttempt(
  15,
  "Tomás Aguirre",
  "B",
  1,
  {
    team: "Patagonia Iron",
    category: "Sub-Junior 16-19",
    age: 18,
    ageDivision: "Sub-Junior 16-19",
    equipment: "Sleeves",
    weightClassKg: "140+",
    location: { city: "Lima", state: "Lima", country: "Peru" },
    bodyweightKg: 146.3,
    instagram: "tomas.lab",
    primaryClassification: { equipment: "Sleeves", event: "Full Powerlifting RAW", division: "Sub-Junior 16-19", weightClass: "140+" },
  },
  [
    { lift: "S", attempts: [no(230), no(230), no(230)] },
    { lift: "B", attempts: [next(150), next(), next()] },
    { lift: "D", attempts: untouched() },
  ],
);

// Nombres completos como vienen del DNI: el diseño tiene que aguantarlos sin recortar letras.
const mariaDeLosAngeles = makeAttempt(
  16,
  "María de los Ángeles Fernández Gutiérrez de la Fuente",
  "S",
  2,
  {
    team: "Rosario Strength",
    category: "Open",
    age: 29,
    ageDivision: "Open",
    equipment: "Bare",
    weightClassKg: "60",
    location: { city: "Rosario", state: "Santa Fe" },
    bodyweightKg: 59.6,
    instagram: "mariadelosangeles.lab",
    primaryClassification: { equipment: "Bare", event: "Full Powerlifting RAW", division: "Open", weightClass: "60" },
    personalBestsKg: { squat: 115, bench: 62.5, deadlift: 140 },
  },
  [
    { lift: "S", attempts: [ok(110), next(117.5), next()] },
    { lift: "B", attempts: untouched() },
    { lift: "D", attempts: untouched() },
  ],
);

const juanManuel = makeAttempt(
  17,
  "Juan Manuel Ignacio de la Santísima Trinidad Rodríguez Echeverría Santángelo",
  "D",
  1,
  {
    team: "Asociación Dominicana de Powerlifting y Levantamientos",
    category: "Master 45-49",
    age: 47,
    ageDivision: "Master 45-49",
    equipment: "Bare",
    weightClassKg: "110",
    location: { city: "Santo Domingo Este", state: "Provincia de Santo Domingo", country: "Dominican Republic" },
    bodyweightKg: 108.7,
    primaryClassification: { equipment: "Bare", event: "Full Powerlifting RAW", division: "Master 45-49", weightClass: "110" },
  },
  [
    { lift: "S", attempts: [ok(220), ok(230), no(237.5)] },
    { lift: "B", attempts: [ok(150), ok(157.5), no(162.5)] },
    { lift: "D", attempts: [next(250), next(), next()] },
  ],
);

export const LAB_SCENARIOS: ReadonlyArray<LabScenario> = [
  {
    id: "banco-en-curso",
    label: "Banco, 2º intento en curso (maqueta)",
    attempt: joaquin,
    reveal: null,
    judgeLights: off,
    clockSeconds: 38,
  },
  {
    id: "banco-valido",
    label: "Banco, 2º intento válido (reveal)",
    attempt: joaquin,
    reveal: "good",
    judgeLights: { J1: "white", J2: "white", J3: "white" },
    clockSeconds: null,
  },
  {
    id: "muerto-record",
    label: "Peso muerto, intento de récord nacional",
    attempt: florencia,
    reveal: null,
    judgeLights: off,
    clockSeconds: 9,
  },
  {
    id: "muerto-nulo",
    label: "Peso muerto, récord nulo (reveal)",
    attempt: florencia,
    reveal: "noLift",
    judgeLights: { J1: "red", J2: "white", J3: "red" },
    clockSeconds: null,
  },
  {
    id: "sentadilla-apertura",
    label: "Sentadilla, apertura",
    attempt: valentina,
    reveal: null,
    judgeLights: off,
    clockSeconds: null,
  },
  {
    id: "sentadilla-nula",
    label: "Sentadilla nula, sigue en banco",
    attempt: tomas,
    reveal: null,
    judgeLights: off,
    clockSeconds: 51,
  },
  {
    id: "solo-banca",
    label: "Solo banca, 3er intento",
    attempt: lucas,
    reveal: null,
    judgeLights: off,
    clockSeconds: 24,
  },
  {
    id: "nombre-largo",
    label: "Nombre largo (54 caracteres)",
    attempt: mariaDeLosAngeles,
    reveal: null,
    judgeLights: off,
    clockSeconds: 44,
  },
  {
    id: "nombre-extremo",
    label: "Nombre extremo (76 caracteres)",
    attempt: juanManuel,
    reveal: null,
    judgeLights: off,
    clockSeconds: 57,
  },
  {
    id: "espera",
    label: "Esperando al siguiente atleta",
    attempt: null,
    reveal: null,
    judgeLights: off,
    clockSeconds: null,
    waitingForNext: true,
  },
];
